import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { FolderLock, CreditCard, ArrowRight } from "lucide-react";

/**
 * Account choice modal — intercepts the global header's "Account" link
 * and asks the visitor whether they want to access their policy documents
 * or pay their premium.
 */
export default function AccountChoiceDialog({ open, onOpenChange }) {
    const navigate = useNavigate();

    const go = (path) => {
        onOpenChange(false);
        navigate(path);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="account-choice-dialog"
                className="max-w-md border border-[rgba(212,175,55,0.35)] bg-[#0b0e13] text-brand-ivory shadow-[0_30px_60px_-20px_rgba(0,0,0,0.85)] sm:max-w-lg"
            >
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl text-brand-ivory">
                        What would you like to do?
                    </DialogTitle>
                    <DialogDescription className="mt-2 text-sm text-brand-muted">
                        Choose the secure flow you need.
                    </DialogDescription>
                </DialogHeader>

                <div className="mt-4 space-y-3">
                    <button
                        type="button"
                        data-testid="account-choice-portal"
                        onClick={() => go("/customer-portal/login")}
                        className="group flex w-full items-center gap-4 rounded-xl border border-[rgba(212,175,55,0.3)] bg-[#0d1117] p-4 text-left transition-colors hover:border-brand-gold/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                    >
                        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[rgba(212,175,55,0.4)] bg-[#0b0e13]">
                            <FolderLock
                                aria-hidden="true"
                                className="h-5 w-5 text-brand-gold2"
                            />
                        </span>
                        <span className="flex-1">
                            <span className="block font-serif text-base text-brand-ivory">
                                Access Policy Documents
                            </span>
                            <span className="mt-1 block text-xs leading-relaxed text-brand-muted">
                                Sign in to your secure document portal with a
                                one-time magic-link sent to your email.
                            </span>
                        </span>
                        <ArrowRight
                            aria-hidden="true"
                            className="h-4 w-4 text-brand-gold2 transition-transform group-hover:translate-x-0.5"
                        />
                    </button>

                    <button
                        type="button"
                        data-testid="account-choice-pay"
                        onClick={() => go("/pay-premium")}
                        className="group flex w-full items-center gap-4 rounded-xl border border-[rgba(212,175,55,0.3)] bg-[#0d1117] p-4 text-left transition-colors hover:border-brand-gold/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
                    >
                        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[rgba(212,175,55,0.4)] bg-[#0b0e13]">
                            <CreditCard
                                aria-hidden="true"
                                className="h-5 w-5 text-brand-gold2"
                            />
                        </span>
                        <span className="flex-1">
                            <span className="block font-serif text-base text-brand-ivory">
                                Pay Insurance Premium
                            </span>
                            <span className="mt-1 block text-xs leading-relaxed text-brand-muted">
                                Pay by debit/credit card, PayPal, or Venmo —
                                no PayPal account required.
                            </span>
                        </span>
                        <ArrowRight
                            aria-hidden="true"
                            className="h-4 w-4 text-brand-gold2 transition-transform group-hover:translate-x-0.5"
                        />
                    </button>
                </div>

                <p className="mt-4 text-[11px] text-brand-muted">
                    New to Endurance Casualty?{" "}
                    <Link
                        to="/contact"
                        onClick={() => onOpenChange(false)}
                        className="text-brand-gold2 underline-offset-4 hover:underline"
                    >
                        Contact a licensed agent →
                    </Link>
                </p>
            </DialogContent>
        </Dialog>
    );
}
